import { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import SupportStrip from "@/components/SupportStrip";
import { useAuth } from "@/lib/auth";
import { api, formatCents, formatDate } from "@/lib/api";
import { PageHeader, Stat, Card, Pill, Loader, Button, EmptyState } from "@/components/ui/Primitives";
import { toast } from "sonner";
import {
  Building2, DollarSign, Wrench, TrendingUp, ArrowRight, FileText,
  CalendarClock, CheckCircle2, AlertCircle, ClipboardList, Receipt,
  Plus, Sparkles, BadgeCheck, ShieldCheck, KeyRound, Shield,
  Users2, Send, Library, ScrollText, Hammer, Calendar as CalIcon, Cpu, ClipboardCheck,
  CreditCard, BarChart3, FileDown, Megaphone, Video, Search, Diamond,
  Newspaper, MessageCircle, Wallet, Calculator, FolderOpen, Star, Zap, Gift,
  BookOpen, Heart, Lock
} from "lucide-react";
import WelcomePackage from "@/components/WelcomePackage";
import DataQualityCard from "@/components/DataQualityCard";
import { AutoChips } from "@/pages/Auto";
import { WalkthroughVideoCard } from "@/components/WalkthroughVideoCard";
import ReferralBanner from "@/components/ReferralBanner";
import TenantSmartDevices from "@/components/TenantSmartDevices";
import DashboardCalendar from "@/components/DashboardCalendar";
import TenantCreditCard from "@/components/TenantCreditCard";
import TenantReferralWidget from "@/components/TenantReferralWidget";
import SmartFeaturesUpsell from "@/components/SmartFeaturesUpsell";
import SmartRecommendations from "@/components/SmartRecommendations";
import SmartAnomalyBanner from "@/components/smart/SmartAnomalyBanner";
import SmartDiscoverFeed from "@/components/smart/SmartDiscoverFeed";
import CopilotEarningsCard from "@/components/CopilotEarningsCard";
import RetentionPulseCard from "@/components/RetentionPulseCard";
import BulkLeaseRenewal from "@/components/BulkLeaseRenewal";
import LandlordRenewalInbox from "@/components/LandlordRenewalInbox";
import SmartChatBubble from "@/components/SmartChatBubble";

const ACTION_GROUPS = [
  {
    label: "Properties & Listings",
    items: [
      { to: "/properties?add=1", icon: Plus, color: "green", title: "Add Property", testid: "tile-add-property" },
      { to: "/tours", icon: Sparkles, color: "green", title: "Smart Listing", testid: "tile-smart-listing" },
      { to: "/inspections", icon: BadgeCheck, color: "blue", title: "Inspections", testid: "tile-inspections" },
      { to: "/compliance", icon: ShieldCheck, color: "green", title: "Compliance", testid: "tile-compliance" },
      { to: "/access-keys", icon: KeyRound, color: "amber", title: "Access Keys", testid: "tile-access-keys" },
      { to: "/insurance", icon: Shield, color: "blue", title: "Insurance", testid: "tile-insurance" },
    ],
  },
  {
    label: "Tenants & Leasing",
    items: [
      { to: "/applications", icon: Users2, color: "green", title: "Leads", testid: "tile-leads" },
      { to: "/applications", icon: Users2, color: "blue", title: "Applications", testid: "tile-applications" },
      { to: "/retention", icon: Heart, color: "green", title: "Long-term tenants", testid: "tile-retention" },
      { to: "/leases?new=1", icon: Send, color: "green", title: "Send Lease", testid: "tile-send-lease" },
      { to: "/leases", icon: Library, color: "green", title: "Lease Templates", testid: "tile-lease-templates" },
      { to: "/messages", icon: MessageCircle, color: "green", title: "Messages", testid: "tile-messages" },
      { to: "/notices", icon: ScrollText, color: "amber", title: "Notices", testid: "tile-notices" },
      { to: "/announcements", icon: Megaphone, color: "green", title: "Announcements", testid: "tile-announcements" },
      { to: "/team", icon: Users2, color: "blue", title: "Your team", testid: "tile-team" },
    ],
  },
  {
    label: "Maintenance & Operations",
    items: [
      { to: "/tickets", icon: Hammer, color: "red", title: "Tickets", testid: "tile-tickets" },
      { to: "/vendors", icon: Wrench, color: "amber", title: "Vendors", testid: "tile-vendors" },
      { to: "/tours", icon: CalIcon, color: "blue", title: "Calendar", testid: "tile-calendar" },
      { to: "/smart-tours", icon: Video, color: "green", title: "Smart Tours", testid: "tile-smart-tours" },
      { to: "/smart-locks", icon: Cpu, color: "green", title: "Smart Locks", testid: "tile-smart-locks" },
      { to: "/inspection", icon: ClipboardCheck, color: "green", title: "Smart Inspection", testid: "tile-inspection", badge: "NEW" },
      { to: "/utilities", icon: Zap, color: "amber", title: "Utilities", testid: "tile-utilities" },
    ],
  },
  {
    label: "Money & Reports",
    items: [
      { to: "/payments", icon: CreditCard, color: "green", title: "Collect Rent", testid: "tile-collect-rent" },
      { to: "/cash-app", icon: Sparkles, color: "green", title: "Smart Cash-App", testid: "tile-cash-app" },
      { to: "/books", icon: BookOpen, color: "green", title: "Books", testid: "tile-books" },
      { to: "/smart-operator", icon: Sparkles, color: "green", title: "Smart Operator", testid: "tile-smart-operator" },
      { to: "/revenue-engine", icon: TrendingUp, color: "green", title: "Revenue Engine", testid: "tile-revenue-engine" },
      { to: "/rent-analysis", icon: TrendingUp, color: "green", title: "Rent Analysis", testid: "tile-rent-analysis" },
      { to: "/expenses", icon: Receipt, color: "amber", title: "Expenses", testid: "tile-expenses" },
      { to: "/reports", icon: BarChart3, color: "blue", title: "Reports", testid: "tile-reports" },
      { to: "/cra-filing", icon: FileDown, color: "green", title: "CRA Filing (T776)", testid: "tile-cra-filing" },
      { to: "/documents", icon: FolderOpen, color: "green", title: "Documents", testid: "tile-documents" },
    ],
  },
  {
    label: "Growth & Marketing",
    items: [
      { to: "/tours", icon: Megaphone, color: "green", title: "Marketing", testid: "tile-marketing" },
      { to: "/tours", icon: Video, color: "green", title: "Video Tours", testid: "tile-video-tours" },
      { to: "/tours", icon: Search, color: "blue", title: "Local SEO", testid: "tile-local-seo" },
      { to: "/investor", icon: Diamond, color: "amber", title: "Investors", testid: "tile-investors" },
      { to: "/referrals", icon: Gift, color: "green", title: "Referrals", testid: "tile-referrals" },
      { to: "/blog", icon: Newspaper, color: "blue", title: "Blog & News", testid: "tile-blog" },
      { to: "mailto:contact@centralrentals.ca", icon: MessageCircle, color: "green", title: "Support", external: true, testid: "tile-support" },
    ],
  },
];

const TILE_TONES = {
  green: "bg-green-100 text-green-700",
  blue: "bg-blue-100 text-blue-700",
  amber: "bg-amber-100 text-amber-700",
  red: "bg-red-100 text-red-700",
};

const PREMIUM_TILE_IDS = new Set([
  // Operations
  "tile-inspections", "tile-vendors", "tile-access-keys", "tile-smart-home",
  // Smart suite
  "tile-compliance", "tile-utilities", "tile-rent-analysis", "tile-investors",
  // Marketing
  "tile-marketing", "tile-video-tours", "tile-local-seo", "tile-smart-listing",
  // Money & docs
  "tile-reports", "tile-tax-export", "tile-expenses", "tile-documents",
  // Comms & legal
  "tile-messages", "tile-notices", "tile-send-lease", "tile-lease-templates",
  // Insurance
  "tile-insurance",
]);

function ActionHub() {
  const { user } = useAuth();
  const isSubscribed = user?.subscription_status === "active" || user?.subscription_status === "trialing";
  const [quizAnswers, setQuizAnswers] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Show a "welcome back — Stripe connected" toast when the landlord
  // lands here from Stripe onboarding (`return_url=/dashboard?connect=return&welcome=1`).
  // We strip the params after firing so a refresh doesn't re-toast.
  useEffect(() => {
    if (searchParams.get("connect") === "return") {
      const welcome = searchParams.get("welcome") === "1";
      toast.success(welcome
        ? "Welcome back — Stripe is connected. Rent will now flow directly to your bank."
        : "Stripe connected.");
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete("connect");
      cleaned.delete("welcome");
      setSearchParams(cleaned, { replace: true });
    } else if (searchParams.get("connect") === "refresh") {
      toast.info("Stripe onboarding paused — resume anytime from the payments tab.");
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete("connect");
      setSearchParams(cleaned, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch the user's onboarding-quiz answers once. We use them to reorder the
  // dashboard sections so the operator's top pain shows up first.
  useEffect(() => {
    const email = user?.email;
    if (!email) return;
    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    fetch(`${API}/api/onboarding/quiz/status?email=${encodeURIComponent(email)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.completed) setQuizAnswers(d.answers || {}); })
      .catch(() => { /* noop — non-personalized order is fine */ });
  }, [user?.email]);

  // Map quiz answers → ordered list of group labels (most relevant first).
  // Unspecified labels keep their original position after these.
  const orderedGroups = useMemo(() => {
    if (!quizAnswers) return ACTION_GROUPS;
    const pain = quizAnswers.top_pain;
    const goal = quizAnswers.goal_30d;
    const priority = [];
    if (pain === "rent_collection" || pain === "taxes" || goal === "audit_ready") priority.push("Money & Reports");
    if (pain === "maintenance") priority.push("Maintenance & Operations");
    if (pain === "leases") priority.push("Tenants & Leasing");
    if (pain === "marketing" || goal === "scale") priority.push("Growth & Marketing");
    if (goal === "switch") priority.push("Properties & Listings");
    const seen = new Set();
    const ordered = [];
    priority.forEach((label) => {
      if (seen.has(label)) return;
      const grp = ACTION_GROUPS.find((g) => g.label === label);
      if (grp) { ordered.push(grp); seen.add(label); }
    });
    ACTION_GROUPS.forEach((g) => { if (!seen.has(g.label)) ordered.push(g); });
    return ordered;
  }, [quizAnswers]);

  return (
    <div className="mt-8 space-y-7" data-testid="landlord-action-hub">
      {/* Auto copilot — reads the operator's setup and suggests the next
          3-5 best actions. Falls back gracefully when Auto is unreachable. */}
      <AutoChips
        entity="dashboard"
        goal="get the operator to their next-best action for their portfolio today"
        context={{
          role: "landlord",
          top_pain: quizAnswers?.top_pain,
          goal_30d: quizAnswers?.goal_30d,
          subscribed: isSubscribed,
        }}
        onAction={(c) => toast(`Auto suggests: ${c.label}`, { description: c.reason })}
      />
      {quizAnswers && (
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700 flex items-center gap-1.5"
          data-testid="dashboard-personalized-pill">
          <Sparkles className="w-3 h-3"/> Personalized for your setup
        </div>
      )}
      {orderedGroups.map((g) => {
        const visible = g.items.filter((t) => isSubscribed || !PREMIUM_TILE_IDS.has(t.testid));
        if (visible.length === 0) return null;
        return (
          <div key={g.label}>
            <h3 className="font-display text-base font-bold text-slate-900 mb-3">{g.label}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {visible.map((t) => {
                const Icon = t.icon;
                const inner = (
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 card-lift flex flex-col items-center justify-center text-center min-h-[112px]">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${TILE_TONES[t.color] || TILE_TONES.green}`}>
                      <Icon className="w-6 h-6"/>
                    </div>
                    <div className="mt-2.5 text-sm font-semibold text-slate-900">{t.title}</div>
                  </div>
                );
                return t.external ? (
                  <a key={t.testid} href={t.to} target="_blank" rel="noreferrer" data-testid={t.testid}>{inner}</a>
                ) : (
                  <Link key={t.testid} to={t.to} data-testid={t.testid}>{inner}</Link>
                );
              })}
            </div>
          </div>
        );
      })}
      {!isSubscribed && (
        <Link to="/billing" data-testid="dashboard-unlock-premium"
          className="block p-5 sm:p-6 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-sky-50 hover:from-emerald-100 hover:to-sky-100 transition-colors shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Premium suite</div>
              <div className="font-display text-base sm:text-lg font-bold text-slate-900 mt-1 leading-snug">
                Unlock Reports, Messages, Documents, Compliance, Utilities, Vendors, Tours, Notices &amp; more
              </div>
              <p className="text-sm text-slate-600 mt-1">Available with any active plan. Try free for 30 days.</p>
            </div>
            <div className="px-4 py-2 rounded-md bg-emerald-600 text-white font-semibold text-sm flex-none shadow-sm self-start sm:self-center inline-flex items-center justify-center">See plans →</div>
          </div>
        </Link>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  // Ask Mark surfaces globally on the dashboard for onboarding help
  // during a landlord's first weeks. Rendered alongside the role-specific
  // dashboard so it appears whether they're a tenant or a landlord.
  const inner =
    user?.role === "tenant" ? <TenantDashboard /> :
    user?.role === "investor" ? <Navigate to="/investor" replace /> :
    <LandlordDashboard />;
  return (
    <>
      {inner}
      {/* Ask Mark bubble is redundant for authed landlords & tenants —
          the Copilot Omnibar FAB (mounted globally in App.js) now
          covers the same "ask anything" flow. Keep it for the public
          hero pages only; hide inside the dashboard to stop the
          double-FAB stack the user reported. */}
      {user?.role !== "investor" && user?.role !== "landlord" && user?.role !== "tenant" && <SmartChatBubble />}
    </>
  );
}

function LandlordDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [applications, setApplications] = useState([]);
  const [properties, setProperties] = useState([]);
  const [smartUsage, setSmartUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [d, t, a, p, u] = await Promise.all([
          api.get("/dashboard/landlord").catch(() => ({ data: null })),
          api.get("/tickets").catch(() => ({ data: [] })),
          api.get("/applications").catch(() => ({ data: [] })),
          api.get("/properties").catch(() => ({ data: [] })),
          api.get("/smart/usage").catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        const fresh = {
          data: d.data,
          tickets: Array.isArray(t.data) ? t.data : (t.data?.tickets || []),
          applications: Array.isArray(a.data) ? a.data : (a.data?.applications || []),
          properties: Array.isArray(p.data) ? p.data : (p.data?.properties || []),
          smartUsage: u.data,
        };
        setData(fresh.data);
        setTickets(fresh.tickets);
        setApplications(fresh.applications);
        setProperties(fresh.properties);
        setSmartUsage(fresh.smartUsage);
        // Best-effort engagement snapshot so the Day-3 + Weekly emails can
        // interpolate the landlord's real portfolio numbers. Never blocks UX.
        try {
          await api.post("/engagement/snapshot", {
            role: "landlord",
            properties_count:   d.data?.properties_count ?? 0,
            tenants_count:      d.data?.tenants_count ?? d.data?.units_occupied ?? 0,
            vacant_units:       Math.max(0, (d.data?.units_total ?? 0) - (d.data?.units_occupied ?? 0)),
            open_maintenance:   d.data?.open_tickets ?? 0,
            rent_collected_pct: d.data?.rent_collection_pct ?? d.data?.occupancy_rate ?? null,
          });
        } catch { /* silent — analytics only */ }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return <Loader/>;

  const firstName = user?.name?.split(" ")[0] || "Landlord";
  // Data-driven anomaly banner — silent when nothing's off, so no false alarms.
  const anomalyMetrics = data ? {
    rent_this_month:     data.rent_collected_this_month_cents,
    rent_last_month:     data.rent_collected_last_month_cents,
    leases_expiring_14d: data.leases_expiring_14d ?? 0,
    open_tickets_7d:     tickets.filter((t) => t?.status !== "closed" && t?.age_days > 7).length,
    tenants_5d_late:     data.tenants_5d_late ?? 0,
  } : null;

  return (
    <div>
      {/* Modern dark-gradient hero — matches Leasing Assistant / Marketing
          Hub / RUBS / Eviction / Key Wall so the whole landlord shell reads
          as one product. Kept above the widget grid so nothing below moves. */}
      <div className="mb-6 -mx-4 sm:-mx-6 rounded-b-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white overflow-hidden" data-testid="landlord-dash-hero">
        <div className="px-5 sm:px-8 pt-8 pb-9 max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-300">
            <Sparkles className="w-3.5 h-3.5"/> Landlord command centre
          </div>
          <h1 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Hello, <span className="text-emerald-400">{firstName}</span>.
          </h1>
          <p className="mt-3 text-base text-slate-300 leading-relaxed max-w-2xl">Your Canadian portfolio, ready when you are — collect rent, sign leases, and answer every lead from one screen.</p>
          <div className="mt-6 flex flex-wrap items-center gap-2.5">
            <Link to="/leases" data-testid="dash-leases-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-extrabold bg-emerald-500 hover:bg-emerald-400 text-slate-950 transition-colors shadow-lg shadow-emerald-500/25">
              Manage leases <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/properties" data-testid="dash-properties-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors">
              View properties
            </Link>
            <Link to="/leasing-assistant"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors">
              Leasing assistant
            </Link>
          </div>
        </div>
      </div>

      <WelcomePackage/>
      <ReferralBanner/>

      {/* Copilot ROI ledger — quantifies what the subscription earned
          this month. Silent when there's nothing above CA$50 total. */}
      <CopilotEarningsCard />

      {/* Rule-based anomaly detector (zero LLM cost). Silent when
          nothing's off — only shows if there's an actionable trend. */}
      <SmartAnomalyBanner metrics={anomalyMetrics} />

      {/* Google-Discover-style "For you" feed — a query-free
          recommendation surface ranked from the landlord's own data
          plus the freshest editorial posts. No LLM cost. */}
      <SmartDiscoverFeed
        data={data}
        tickets={tickets}
        applications={applications}
        properties={properties}
      />

      <SmartFeaturesUpsell/>

      <SmartRecommendations
        data={data}
        tickets={tickets}
        applications={applications}
        properties={properties}
        isSubscribed={user?.subscription_status === "active" || user?.subscription_status === "trialing"}
      />

      <WalkthroughVideoCard role="landlord" />

      {/* Auto-Correctness · Accuracy · Completeness · Consistency · Timeliness.
          Rule-based dashboard tile so landlords see exactly which data is
          dragging their portfolio score down before a lease/audit needs it. */}
      <DataQualityCard />


      {smartUsage && smartUsage.total_calls > 0 && (
        <Link to="/smart" data-testid="smart-usage-tile"
          className="group block rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-200 hover:border-emerald-300 hover:shadow-md transition-all p-5 mb-2">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center flex-none shadow-md">
              <Sparkles className="w-5 h-5"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Smart features · ROI</div>
              <div className="font-display text-sm sm:text-base font-bold text-slate-950 mt-1 leading-snug">
                Smart features ran <span data-testid="smart-usage-month-calls" className="text-emerald-700">{smartUsage.this_month_calls ?? 0}</span> {(smartUsage.this_month_calls === 1) ? "time" : "times"} this month
                <span className="hidden sm:inline"> — saved you ~<span data-testid="smart-usage-hours" className="text-emerald-700">{smartUsage.hours_saved_this_month ?? 0}</span> hours</span>
              </div>
              <div className="sm:hidden text-xs text-slate-600 mt-1">~{smartUsage.hours_saved_this_month ?? 0} hours saved this month</div>
              <div className="hidden sm:block text-xs text-slate-500 mt-1">{smartUsage.total_calls} lifetime runs · ~{smartUsage.hours_saved_total} hours saved overall</div>
            </div>
            <ArrowRight className="w-4 h-4 text-emerald-700 flex-none group-hover:translate-x-0.5 transition-transform"/>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-properties" label="Properties" value={data?.properties_count ?? 0} sub={`${data?.units_total ?? 0} units total`} icon={Building2}/>
        <Stat testid="stat-occupancy" label="Occupancy" value={`${Math.round(data?.occupancy_rate || 0)}%`} sub={`${data?.units_occupied || 0} of ${data?.units_total || 0} leased`} icon={TrendingUp}/>
        <Stat testid="stat-rent-collected" label="Rent collected" value={formatCents(data?.rent_collected || 0)} sub="Lifetime" icon={DollarSign} accent="text-green-800"/>
        <Stat testid="stat-open-tickets" label="Open tickets" value={data?.open_tickets ?? 0} sub="Active maintenance" icon={Wrench}/>
      </div>

      {/* Bulk lease renewal — one-click way to renew many tenants at once.
          Sits between the KPI stats and the recent-activity cards so it's
          impossible to miss when the landlord returns after end-of-lease. */}
      <div className="mt-4 space-y-4">
        <BulkLeaseRenewal />
        <LandlordRenewalInbox />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Recent tickets</h3>
              <p className="text-sm text-slate-500">Latest maintenance activity</p>
            </div>
            <Link to="/tickets" className="text-sm font-semibold text-green-800 hover:underline" data-testid="dash-tickets-link">See all →</Link>
          </div>
          {tickets.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">No tickets yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {tickets.slice(0, 5).map((t) => (
                <Link key={t.ticket_id} to={`/tickets/${t.ticket_id}`} data-testid={`recent-ticket-${t.ticket_id}`}
                  className="flex items-center justify-between py-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{formatDate(t.created_at)} · Priority {t.priority}</div>
                  </div>
                  <TicketStatusPill status={t.status}/>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Applications</h3>
              <p className="text-sm text-slate-500">Recent applicants</p>
            </div>
            <Link to="/applications" className="text-sm font-semibold text-green-800 hover:underline" data-testid="dash-applications-link">See all →</Link>
          </div>
          {applications.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">No applications yet.</div>
          ) : (
            <div className="space-y-3">
              {applications.slice(0, 5).map((a) => (
                <div key={a.application_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">{a.full_name}</div>
                    <div className="text-xs text-slate-500 truncate">{a.email}</div>
                  </div>
                  <Pill tone={a.status === "approved" ? "green" : a.status === "rejected" ? "red" : "amber"}>{a.status}</Pill>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6 grid md:grid-cols-4 gap-5">
        <QuickAction to="/properties" icon={Building2} title="Add property" sub="List a new building"/>
        <QuickAction to="/leases" icon={FileText} title="Create lease" sub="Draft a tenancy agreement"/>
        <QuickAction to="/community" icon={MessageCircle} title="Community" sub="Network + find vendors"/>
        <QuickAction to="/resources" icon={Star} title="Resources" sub="Province guides, CRA forms, more"/>
      </div>

      <RetentionPulseCard/>

      <ActionHub/>

      <div className="mt-7">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Recent properties</h2>
            <p className="text-sm text-slate-500">Your latest listings.</p>
          </div>
          <Link to="/properties" className="text-sm font-semibold text-green-800 hover:underline" data-testid="dash-recent-properties-link">See all →</Link>
        </div>
        {properties.length === 0 ? (
          <Card className="p-8 text-center" data-testid="recent-properties-empty">
            <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-3"/>
            <div className="text-sm text-slate-500">No properties yet.</div>
            <Link to="/properties"><Button className="mt-4">Add your first property</Button></Link>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {properties.slice(0, 4).map((p) => (
              <Link key={p.property_id} to={`/properties/${p.property_id}`} data-testid={`recent-property-${p.property_id}`}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden card-lift">
                <div className="aspect-[5/3] bg-slate-100 overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300"><Building2 className="w-9 h-9"/></div>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-display font-bold text-slate-900 truncate text-[15px]">{p.name}</div>
                  <div className="mt-1 text-xs text-slate-500 truncate">{p.address}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <Pill tone="slate">{p.units_total || 0} {p.units_total === 1 ? "unit" : "units"}</Pill>
                    <span className="text-xs text-slate-400">{formatDate(p.created_at)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <SupportStrip testid="landlord-support-strip"/>
      <DashboardCalendar role="landlord"/>
    </div>
  );
}

function TenantDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dues, setDues] = useState({ lease: null, dues: [] });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  // e-Transfer / cash / cheque manual-payment modal state.
  const [etModal, setEtModal] = useState(false);
  const [etMethod, setEtMethod] = useState("e_transfer");
  const [etReference, setEtReference] = useState("");
  const [etSubmitting, setEtSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [d, du, t] = await Promise.all([
          api.get("/dashboard/tenant").catch(() => ({ data: null })),
          api.get("/payments/dues").catch(() => ({ data: { lease: null, dues: [] } })),
          api.get("/tickets").catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        setData(d.data);
        setDues(du.data || { lease: null, dues: [] });
        setTickets(Array.isArray(t.data) ? t.data : (t.data?.tickets || []));
        // Best-effort engagement snapshot — fuels the tenant Day-3 + Weekly emails.
        try {
          const lease = du.data?.lease || d.data?.lease;
          const nextDue = (du.data?.dues || []).find((x) => x.status !== "paid") || d.data?.next_due;
          await api.post("/engagement/snapshot", {
            role: "tenant",
            next_rent_due:        nextDue?.due_date || nextDue?.period || null,
            rent_amount_cad:      (nextDue?.amount || nextDue?.amount_due || lease?.monthly_rent || 0) / 100,
            lease_days_remaining: lease?.end_date
              ? Math.max(0, Math.round((new Date(lease.end_date) - Date.now()) / 86400000))
              : null,
            open_tickets:         (Array.isArray(t.data) ? t.data : (t.data?.tickets || []))
                                    .filter((x) => x.status !== "closed").length,
            last_payment_status:  (du.data?.dues || []).find((x) => x.status === "paid") ? "paid" : null,
          });
        } catch { /* silent */ }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return <Loader/>;

  const next = dues.dues?.find((d) => d.status !== "paid") || data?.next_due;

  // Rent-payment collection via Stripe Connect was removed Feb 2026 —
  // Central Rentals never touches tenant funds. Tenants now view their
  // landlord's payment instructions (Interac / bank / cheque) inside
  // /payments and pay their landlord directly, off-platform.

  // Submit a manual (non-Stripe) payment report for the current due. The
  // landlord will see it as "reported" and confirm it in one tap. This is
  // the escape hatch for tenants who pay by Interac e-Transfer / cash /
  // cheque — the standard flow for most Canadian small landlords.
  const submitManualPayment = async () => {
    if (!next?.due_id) {
      toast.error("No open rent due to report against.");
      return;
    }
    setEtSubmitting(true);
    try {
      await api.post(`/rent-dues/${next.due_id}/record-payment`, {
        method: etMethod,
        reference: etReference || null,
      });
      toast.success("Thanks — we've let your landlord know. They'll confirm shortly.");
      setEtModal(false);
      setEtReference("");
      // Refresh dues so the badge flips to "Reported".
      try {
        const du = await api.get("/payments/dues");
        setDues(du.data || { lease: null, dues: [] });
      } catch { /* silent */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record payment");
    } finally { setEtSubmitting(false); }
  };

  return (
    <div>
      {etModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
             role="dialog" aria-modal="true" data-testid="etransfer-modal"
             onClick={() => !etSubmitting && setEtModal(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold text-slate-900">Report a manual payment</h3>
            <p className="text-sm text-slate-500 mt-1">
              Use this if you paid your rent by Interac e-Transfer, cash, cheque, or EFT. Your landlord will confirm it — usually within a day.
            </p>
            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Method</span>
                <select
                  value={etMethod}
                  onChange={(e) => setEtMethod(e.target.value)}
                  data-testid="etransfer-method-select"
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="e_transfer">Interac e-Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="eft">EFT / Bank transfer</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reference (optional)</span>
                <input
                  value={etReference}
                  onChange={(e) => setEtReference(e.target.value)}
                  data-testid="etransfer-reference-input"
                  placeholder="e.g. confirmation #, cheque #, or memo"
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </label>
              {next?.amount_cents ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800">
                  Reporting rent for <b>{next.period || next.due_date}</b>: <b>{formatCents(next.amount_cents)}</b>
                </div>
              ) : null}
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEtModal(false)}
                disabled={etSubmitting}
                className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                data-testid="etransfer-cancel"
              >Cancel</button>
              <button
                type="button"
                onClick={submitManualPayment}
                disabled={etSubmitting}
                data-testid="etransfer-submit"
                className="px-4 py-2 rounded-md text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
              >{etSubmitting ? "Sending…" : "Send report"}</button>
            </div>
          </div>
        </div>
      )}
      <PageHeader
        title={`Hi, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle="Your rental, simplified."
        tone="modern-dark"
        eyebrow="Renter portal"
        eyebrowIcon={Sparkles}
        actions={
          <>
            <Link to="/messages"><Button variant="secondary" data-testid="dash-messages-link"><MessageCircle className="w-4 h-4"/> Messages</Button></Link>
            <Link to="/payments"><Button data-testid="dash-pay-link">Pay rent</Button></Link>
          </>
        }
      />

      <WelcomePackage/>

      <WalkthroughVideoCard role="tenant" />

      {/* Auto-Correctness — tenants see their own profile-quality score
          so they know exactly what to fill in before a landlord reviews. */}
      <DataQualityCard />


      {/* Next rent due card */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-6 md:col-span-2 bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg shadow-emerald-500/20">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-50">Next rent due</div>
          {next ? (
            <>
              <div className="mt-3 font-display text-5xl font-bold">{formatCents(next.amount || next.amount_due || data?.next_due?.amount || 0)}</div>
              <div className="mt-2 text-emerald-50/85 text-sm">Due {formatDate(next.due_date || next.period || data?.next_due?.due_date)} · pay your landlord directly using their published instructions</div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link to="/payments" data-testid="pay-rent-button"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors shadow-sm">
                  View payment instructions <ArrowRight className="w-4 h-4"/>
                </Link>
                {next?.due_id && (
                  <button onClick={() => setEtModal(true)} data-testid="report-etransfer-button"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-emerald-800/40 backdrop-blur text-white font-semibold ring-1 ring-white/40 hover:bg-emerald-800/60 transition-colors">
                    Report e-Transfer / cash
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="mt-3 font-display text-4xl font-bold">All caught up</div>
              <div className="mt-2 text-emerald-50/85 text-sm">No upcoming rent due.</div>
            </>
          )}
        </Card>

        <Card className="p-6">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Lease</div>
          {data?.lease ? (
            <>
              <div className="mt-2 font-display text-xl font-bold text-slate-900">{formatCents(data.lease.monthly_rent)}/mo</div>
              <div className="text-sm text-slate-500 mt-1">{formatDate(data.lease.start_date)} → {formatDate(data.lease.end_date)}</div>
              <Pill tone={data.lease.status === "signed" ? "green" : "amber"} className="mt-3">{data.lease.status}</Pill>
              <Link to="/leases" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-green-800 hover:underline">
                View lease <ArrowRight className="w-3.5 h-3.5"/>
              </Link>
            </>
          ) : (
            <div className="mt-3 text-sm text-slate-500 leading-relaxed">
              No active lease yet. Once your landlord creates a lease for your account, it will appear here.
            </div>
          )}
        </Card>
      </div>

      {/* Tenant Action Hub — matches mobile dark theme */}
      <TenantActionHub/>

      {/* Active requests */}
      <div className="mt-7">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold text-slate-900">Active requests</h2>
          <Link to="/tickets" className="text-sm font-semibold text-green-800 hover:underline">See all →</Link>
        </div>
        {tickets.length === 0 ? (
          <Card className="p-6 text-center" data-testid="tenant-no-requests">
            <div className="text-sm text-slate-500">No open requests.</div>
            <Link to="/tickets"><Button className="mt-3" variant="secondary"><Wrench className="w-4 h-4"/> Submit a request</Button></Link>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {tickets.slice(0, 4).map((t) => (
              <Link key={t.ticket_id} to={`/tickets/${t.ticket_id}`} data-testid={`tenant-ticket-${t.ticket_id}`}
                className="flex items-center justify-between p-4 rounded-lg bg-white border border-slate-200 card-lift">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{t.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{formatDate(t.created_at)} · Priority {t.priority}</div>
                </div>
                <TicketStatusPill status={t.status}/>
              </Link>
            ))}
          </div>
        )}
      </div>

      <TenantCreditCard email={user?.email}/>
      <TenantReferralWidget/>

      <TenantSmartDevices/>

      <DashboardCalendar role="tenant"/>

      <TenantCommunityCTA/>

      <SupportStrip testid="tenant-support-strip"/>
    </div>
  );
}

const TENANT_GROUPS = [
  {
    label: "Today",
    items: [
      { to: "/tenant/announcements", title: "Announcements", icon: Megaphone, tone: "green" },
      { to: "/tickets?new=1", title: "Request maintenance", icon: Hammer, tone: "amber" },
      { to: "/leases", title: "View lease", icon: FileText, tone: "blue" },
      { to: "/messages", title: "Messages", icon: MessageCircle, tone: "green" },
      { to: "/inspections", title: "Inspections", icon: BadgeCheck, tone: "blue" },
    ],
  },
  {
    label: "Smart home & insurance",
    items: [
      { to: "/smart-home", title: "Smart home", icon: Cpu, tone: "green" },
      { to: "/insurance", title: "Renter's insurance", icon: ShieldCheck, tone: "green" },
    ],
  },
  {
    label: "Money & credit",
    items: [
      { to: "/wellness", title: "Financial wellness", icon: Wallet, tone: "amber" },
      { to: "/rent-calculator", title: "Rent calculator", icon: Calculator, tone: "green" },
      { to: "/credit-builder", title: "Credit coach", icon: TrendingUp, tone: "green" },
      { to: "/documents", title: "My documents", icon: FolderOpen, tone: "green" },
      { to: "/documents?upload=1", title: "Send doc to landlord", icon: Send, tone: "green" },
      { to: "/review", title: "Leave a review", icon: Star, tone: "amber" },
      { to: "/referrals", title: "Refer a friend", icon: Gift, tone: "green" },
    ],
  },
];

const TENANT_TONES = {
  green: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  blue: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
  amber: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
};

function TenantActionHub() {
  return (
    <div className="mt-6 rounded-2xl bg-white border border-slate-200 text-slate-900 p-6 space-y-7 relative overflow-hidden shadow-sm" data-testid="tenant-action-hub">
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
        background: "radial-gradient(700px 320px at 10% 0%, rgba(125,211,252,0.18), transparent 60%), radial-gradient(500px 280px at 95% 95%, rgba(167,139,250,0.14), transparent 60%)"
      }}/>
      {TENANT_GROUPS.map((g) => (
        <div key={g.label} className="relative">
          <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{g.label}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {g.items.map((t, i) => {
              const Icon = t.icon;
              const tid = `tenant-tile-${t.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`;
              return (
                <Link key={`${g.label}-${i}`} to={t.to} data-testid={tid}
                  className="group bg-slate-50 hover:bg-white hover:border-emerald-200 hover:shadow-md border border-slate-200 rounded-2xl p-4 min-h-[120px] flex flex-col justify-between transition-all">
                  <div className={`w-11 h-11 rounded-xl ${TENANT_TONES[t.tone] || TENANT_TONES.green} flex items-center justify-center`}>
                    <Icon className="w-5 h-5"/>
                  </div>
                  <div className="mt-3 font-semibold text-[15px] text-slate-900 leading-tight">{t.title}</div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function TicketStatusPill({ status }) {
  if (status === "resolved") return <Pill tone="green"><CheckCircle2 className="w-3 h-3"/> Resolved</Pill>;
  if (status === "in_progress") return <Pill tone="blue"><CalendarClock className="w-3 h-3"/> In progress</Pill>;
  return <Pill tone="amber"><AlertCircle className="w-3 h-3"/> Open</Pill>;
}

function QuickAction({ to, icon: Icon, title, sub }) {
  return (
    <Link to={to} data-testid={`quick-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex items-center gap-4 p-5 rounded-xl bg-white border border-slate-200 card-lift">
      <div className="w-11 h-11 rounded-xl bg-green-50 text-green-800 flex items-center justify-center flex-none">
        <Icon className="w-5 h-5"/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400"/>
    </Link>
  );
}

function TenantCommunityCTA() {
  const { user } = useAuth();
  const uid = user?.user_id || user?.id;
  const [alertCount, setAlertCount] = useState(0);
  useEffect(() => {
    if (!uid) return;
    const since = localStorage.getItem("tenant-listings-lastseen") || "";
    const qs = since ? `?since=${encodeURIComponent(since)}` : "";
    fetch(`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/listings-alerts/count${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
    }).then(r => r.json()).then(d => setAlertCount(d.count || 0)).catch(() => {});
  }, [uid]);
  return (
    <section className="grid md:grid-cols-2 gap-4" data-testid="tenant-community-cta">
      <Link to="/tenant-community" className="group block p-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg hover:shadow-xl transition" data-testid="tenant-community-link">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Tenant community</div>
            <div className="font-display text-xl font-extrabold mt-1">Meet other Canadian tenants →</div>
            <div className="text-sm opacity-90 mt-2">Share ideas, find roommates, get neighbourhood tips. Build your tenant bio so prospective landlords get to know you.</div>
          </div>
          <MessageCircle className="w-6 h-6 opacity-90"/>
        </div>
      </Link>
      <Link to="/tenant-community?tab=listings" className="group block p-5 rounded-2xl bg-white border border-slate-200 hover:border-emerald-300 transition" data-testid="tenant-listings-cta">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">New listings</div>
            <div className="font-display text-xl font-extrabold text-slate-900 mt-1">
              {alertCount > 0 ? `${alertCount} new rental${alertCount === 1 ? "" : "s"} this week` : "Browse all rentals →"}
            </div>
            <div className="text-sm text-slate-600 mt-2">Listings posted by landlords across every Canadian province. Filter by city, price, beds, pets.</div>
          </div>
          {alertCount > 0 && (
            <div className="ml-3 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold tabular-nums" data-testid="tenant-listings-count">{alertCount}</div>
          )}
        </div>
      </Link>
    </section>
  );
}


export { TicketStatusPill };
